import React, { useState, useContext } from 'react';
import { AtCircle } from '@styled-icons/ionicons-solid';
import { AuthContext } from 'contexts/AuthContext';
import { useForm } from 'react-hook-form';
import { ClipLoader } from 'react-spinners';
import { ChangePassword } from 'pages/Settings/SettingsPage/SettingsAccount/ChangePassword';
import { InlineError } from 'ui/InlineError';
import { TotemButton } from 'ui/Button';
import { handleTextInput, validateEmail } from 'utils';
import { TotemLabel, TotemInput } from 'styles';
import {
    SettingsTitleContainerWrapper,
    SettingsTitleContainer,
    SettingsTitle,
    SettingsCard,
    SettingsCredentialsSubtitle,
    CredentialsSubtitleContainer,
    Subtext,
} from 'styles/pages/settings.styles';

import { analyticsEvents, track } from 'tracking/segment';
import type { AuthContextType } from 'contexts/AuthContext';
import { UserFragmentFragment as User, useCheckPasswordMutation, useUpdateEmailMutation } from 'data/__generated__';

export interface LoginCredentialsForm {
    emailAddress: string | undefined;
    password: string;
}
export const LoginCredentials: React.FC<{ username: User['username'] }> = ({ username }) => {
    const [emailValues, setEmailValues] = useState<LoginCredentialsForm>({ emailAddress: username, password: '' });
    const {
        authActions: { logout },
    } = useContext(AuthContext) as AuthContextType;
    const [updateEmail, { loading: emailLoading }] = useUpdateEmailMutation();
    const [checkPassword, { loading: checkLoading }] = useCheckPasswordMutation();
    const {
        register,
        clearErrors,
        handleSubmit,
        formState: { errors },
        setError,
        setValue,
    } = useForm<LoginCredentialsForm>({
        reValidateMode: 'onSubmit',
        defaultValues: { emailAddress: username, password: '' },
    });

    const emailFieldsFilled = emailValues.password && emailValues.emailAddress && emailValues.emailAddress !== username;

    const isLoading = emailLoading || checkLoading;

    const handleUpdateEmail = async () => {
        const { password, emailAddress } = emailValues;
        const trimmedEmailAddress = (emailAddress ?? '').trim();

        const checkPasswordResult = await checkPassword({ variables: { password } });
        const passwordIsCorrect = checkPasswordResult.data?.checkPassword;

        if (!passwordIsCorrect) {
            const message = 'Mot de passe incorrect';
            setError('password', { type: 'invalid', message });
            return;
        }

        try {
            const updateEmailResult = await updateEmail({
                variables: { emailAddress: trimmedEmailAddress },
                refetchQueries: ['GET_USER', 'GET_ORGANIZATION'],
            });
            const response = updateEmailResult.data?.updateEmail;

            if (response && response.success) {
                track(analyticsEvents.UPDATE_EMAIL);
                setValue('password', '');
                setEmailValues({ emailAddress, password: '' });
            }
        } catch (error: any) {
            setError('emailAddress', { type: 'invalid', message: error.message.split(': ').pop() });
        }
    };

    const handleEmailInputs = (e: React.ChangeEvent<HTMLInputElement>) => {
        clearErrors(e.target.name as 'emailAddress' | 'password');
        handleTextInput(e, setEmailValues, emailValues);
    };

    const handleForgottenPassword = async () => {
        logout();
        window.location.replace('/recover-password');
    };

    return (
        <SettingsCard>
            <SettingsTitleContainerWrapper>
                <SettingsTitleContainer>
                    <AtCircle />
                    <SettingsTitle>Mes identifiants</SettingsTitle>
                </SettingsTitleContainer>
            </SettingsTitleContainerWrapper>
            <form onSubmit={handleSubmit(handleUpdateEmail)}>
                <CredentialsSubtitleContainer>
                    <SettingsCredentialsSubtitle $noMargin>Changer l'adresse email</SettingsCredentialsSubtitle>
                    <TotemButton $fontSize="1em" disabled={!emailFieldsFilled} type="submit" $isLoading={isLoading}>
                        {isLoading ? <ClipLoader color="white" size="16px" /> : 'Mettre à jour'}
                    </TotemButton>
                </CredentialsSubtitleContainer>
                <TotemLabel>
                    Email de connexion <InlineError display={errors.emailAddress} />
                    <TotemInput
                        placeholder="Entrez votre email de connexion"
                        type="email"
                        {...register('emailAddress', {
                            required: true,
                            validate: (value) => validateEmail('Votre mail n’est pas valide')(value || ''),
                        })}
                        onChange={handleEmailInputs}
                    />
                </TotemLabel>
                <TotemLabel>
                    Mot de passe <InlineError display={errors.password} />
                    <TotemInput
                        placeholder="Entrez votre mot de passe"
                        type="password"
                        autoComplete="current-password"
                        {...register('password', { required: true })}
                        onChange={handleEmailInputs}
                    />
                </TotemLabel>
                <Subtext onClick={handleForgottenPassword}>Mot de passe oublié ?</Subtext>
            </form>
            <ChangePassword />
        </SettingsCard>
    );
};
